import { createFilterContext } from '../../common/filter/createFilterContext';
import { PassStatus } from '../../api/models/PassStatus';
import { PassPaymentStatus } from '../../api/models/PassPaymentStatus';
import { PassType } from '../../api/models/PassType';

// PENDING_PAYMENT is intentionally omitted - pending-payment passes are transient state
// and should never appear in the passes list, so they can't be a filter option either.
export const ALL_PASS_STATUSES: PassStatus[] = [
  PassStatus.ACTIVE,
  PassStatus.SUSPENDED,
  PassStatus.ARCHIVED,
  PassStatus.EXPIRED,
];

export const ALL_PASS_PAYMENT_STATUSES: PassPaymentStatus[] = [
  PassPaymentStatus.PAID,
  PassPaymentStatus.MANUAL,
  PassPaymentStatus.CONFIRMED,
];

export const ALL_PASS_TYPES: PassType[] = [
  PassType.PUNCH_BASED,
  PassType.UNLIMITED_MONTHLY,
  PassType.FIXED_SCHEDULE_MONTHLY,
];

export const EXPIRING_SOON_DAYS = 10;

// null inside locationIds represents pass templates whose redeemable_on order pages
// don't share a single location - "undetermined".
export type LocationFilterValue = string | null;

type PassesFilterState = {
  statuses: PassStatus[];
  paymentStatuses: PassPaymentStatus[];
  passTypes: PassType[];
  orderPageIds: string[] | null;
  locationIds: LocationFilterValue[] | null;
  atRiskLocationIds: LocationFilterValue[] | null;
};

const DEFAULTS: PassesFilterState = {
  statuses: ALL_PASS_STATUSES,
  paymentStatuses: ALL_PASS_PAYMENT_STATUSES,
  passTypes: ALL_PASS_TYPES,
  orderPageIds: null,
  locationIds: null,
  atRiskLocationIds: null,
};

const { Provider: PassesFilterProvider, useBaseFilter } = createFilterContext<PassesFilterState>({
  storageKey: 'webook-passes-filter',
  defaults: DEFAULTS,
  isActive: (s) =>
    s.statuses.length !== ALL_PASS_STATUSES.length ||
    s.paymentStatuses.length !== ALL_PASS_PAYMENT_STATUSES.length ||
    s.passTypes.length !== ALL_PASS_TYPES.length ||
    s.orderPageIds !== null ||
    s.locationIds !== null,
});

export { PassesFilterProvider };

export const usePassesFilter = () => {
  const { state, setState, isActive } = useBaseFilter();
  return {
    statuses: state.statuses,
    setStatuses: (v: PassStatus[]) => setState({ statuses: v }),
    paymentStatuses: state.paymentStatuses,
    setPaymentStatuses: (v: PassPaymentStatus[]) => setState({ paymentStatuses: v }),
    passTypes: state.passTypes,
    setPassTypes: (v: PassType[]) => setState({ passTypes: v }),
    orderPageIds: state.orderPageIds,
    setOrderPageIds: (v: string[] | null) => setState({ orderPageIds: v }),
    locationIds: state.locationIds,
    setLocationIds: (v: LocationFilterValue[] | null) => setState({ locationIds: v }),
    atRiskLocationIds: state.atRiskLocationIds,
    setAtRiskLocationIds: (v: LocationFilterValue[] | null) => setState({ atRiskLocationIds: v }),
    isAtRiskFilterActive: state.atRiskLocationIds !== null,
    resetAtRiskFilter: () => setState({ atRiskLocationIds: null }),
    state,
    isFilterActive: isActive,
    resetFilter: () => setState({ ...DEFAULTS, atRiskLocationIds: state.atRiskLocationIds }),
  };
};
